import {
  ButtonHTMLAttributes,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

type CollapsibleContextValue = [{ [x: string]: boolean }, (id: string) => void];

export interface CollapsibleProps {
  children: ReactNode;
  allowMultiple: boolean;
  sectionid?: string | null;
}

export interface CollapsibleControlProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  bookingid: string;
}

export interface CollapsibleContentProps {
  children: ReactNode;
  bookingid: string;
}

const CollapsibleContext = createContext<CollapsibleContextValue | null>(null);

export function useCollapsible(): CollapsibleContextValue {
  const context = useContext(CollapsibleContext);

  if (!context)
    throw new Error(
      'Collapsible context must be consumed within the Collapsible component',
    );

  return context;
}

export function Collapsible({
  children,
  allowMultiple,
  sectionid,
}: CollapsibleProps): JSX.Element {
  const [openSection, setOpenSection] = useState<{ [x: string]: boolean }>(
    sectionid ? { [sectionid]: true } : {},
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('id')) {
      setOpenSection({ [searchParams.get('id') as string]: true });
    }
  }, []);

  function handleToggle(id: string) {
    const isOpen = !!openSection[id];
    if (allowMultiple) {
      setOpenSection(prevValue => ({ ...prevValue, [id]: !isOpen }));
    } else {
      if (!isOpen) {
        navigate(`${location.pathname}?id=${id}`);
      } else {
        navigate(location.pathname);
      }
      setOpenSection({ [id]: !isOpen });
    }
  }

  return (
    <CollapsibleContext.Provider value={[openSection, handleToggle]}>
      {children}
    </CollapsibleContext.Provider>
  );
}

export function CollapsibleContent({
  children,
  ...props
}: CollapsibleContentProps): JSX.Element {
  const { bookingid } = props;
  const [openSection] = useCollapsible();

  return (
    <div
      id="doc-collapsible-content"
      hidden={!openSection[bookingid]}
      {...props}
    >
      {children}
    </div>
  );
}

export function CollapsibleControl({
  children,
  ...props
}: CollapsibleControlProps): JSX.Element {
  const { bookingid } = props;
  const [openSection, handleToggle] = useCollapsible();

  return (
    <button
      aria-expanded={openSection[bookingid]}
      aria-controls="doc-collapsible-content"
      onClick={() => handleToggle(bookingid)}
      {...props}
    >
      {children}
    </button>
  );
}
